import { useState, useEffect } from 'react';
import { APP_MODES } from '../constants/app';
import { AUTH } from '../constants/siteConstants';
import { getUserAuthData, setUserInStorage } from '../actions/users/authMethods';
import setAuthorizationToken from '../utils/setAuthorizationToken';
import { useLocation, useNavigate } from 'react-router-dom';
import routes from '../routes';
import Session from '../utils/session';
import Dates from '../utils/dates';

const prestadoresSmgAuth = Session.getAuth();
const token = Session.getAuthenticationToken();
const exp = Session.getTokenExpirationTime();
const userExternalData = Session.getExternalAuth();
const userPermissions = Session.getPermissions();

const checkVisitorType = () => {
  if (token && exp && prestadoresSmgAuth && userPermissions && !userExternalData)
    return APP_MODES.DEFAULT;
  if (userExternalData && userExternalData.modelEspecifico?.soloLectura) return APP_MODES.READ_ONLY;
  if (userExternalData && userExternalData.modelEspecifico?.impersonado)
    return APP_MODES.IMPERSONATED;
};

const useAppMode = () => {
  const visitorType = checkVisitorType();
  const [authData, setAuthData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const configureAppMode = async () => {
      //user mode.
      if (visitorType === APP_MODES.DEFAULT) {
        const isTokenExpDateValid = Session.checkSessionTokenExpiration(exp);

        if (isTokenExpDateValid) {
          const userHasAcceptedTerms = Session.getAcceptedTerms();
          const userHasDOC24Permission = Session.getDoc24Permission();
          const userSurveyResponse = Session.getSurveyResponse();
          const isCircle = Session.getUserIsCircle();
          const managementUnit = Session.getManagementUnits();

          setAuthData({
            statusCode: 200,
            authData: {
              userPermissions,
              userDetail: prestadoresSmgAuth,
              token,
              userHasDOC24Permission,
              userHasAcceptedTerms,
              userSurveyResponse,
              isCircle,
              managementUnit,
            },
          });
          setAuthorizationToken(token);
        } else {
          Session.remove();

          setAuthData({
            statusCode: 401,
            error: true,
            message: AUTH.TOKEN_EXPIRED,
          });
        }
      }
      // impersonated
      if (visitorType === APP_MODES.IMPERSONATED || visitorType === APP_MODES.READ_ONLY) {
        setIsLoading(true);
        const formatExp = Dates.get(userExternalData.exp, 'YYYYMMDDHHmmss').toDate();
        const isTokenExpDateValid = Session.checkSessionTokenExpiration(formatExp);

        if (isTokenExpDateValid) {
          try {
            const { statusCode, authData } = await getUserAuthData(userExternalData);
            if (statusCode === 200) {
              const {
                userDetail,
                userPermissions,
                token,
                tokenExpirationDate,
                userHasDOC24Permission,
                userHasAcceptedTerms,
                userSurveyResponse,
                isCircle,
                managementUnit,
              } = authData;
              setUserInStorage(
                userDetail,
                token,
                tokenExpirationDate,
                userPermissions,
                userHasAcceptedTerms,
                userHasDOC24Permission,
                userSurveyResponse,
                isCircle,
                managementUnit,
              );

              setAuthData({
                statusCode,
                authData,
              });
            }
          } catch (error) {
            setAuthData({
              statusCode: 401,
              error,
              error: true,
              message: AUTH.IMPERSONATE_UNAUTHORIZED,
            });
          } finally {
            Session.removeExternalAuth();
          }
        } else {
          Session.remove();

          setAuthData({
            statusCode: 401,
            error: true,
            message: AUTH.TOKEN_EXPIRED,
          });

          Session.removeExternalAuth();
        }
        setIsLoading(false);
      }
      const currentLocation = location.pathname.replace('/', '');
      const isPrivateRoute = routes.filter((route) => currentLocation === route.path)[0]?.isPrivate;
      if (!visitorType && isPrivateRoute) navigate('/');
    };

    configureAppMode();
  }, []);

  return [authData, visitorType, isLoading];
};

export default useAppMode;
